// sqlite value-slot adapter (T12, Docs/AutocompleteValueSlot-Plan.md) —
// sqlite clone of the T8 pgsql exemplar (pgsqlValueSlotAdapter.ts). Turns the
// expression arm's existing column discovery into normalized
// ValueSlotColumnDescriptors for the shared policy (D10). CTE/derived
// projection goes through the SAME machinery the arm uses
// (`resolveCteColumns` / `inferSubqueryColumns`) — not reimplemented.
//
// Relation lookup mirrors the resolver's own private `resolveRelation`
// (`sqliteResolver.ts`): sqlite has no search_path — a binding's database
// qualifier (`binding.database ?? binding.schema`, an ATTACHed-db alias)
// matches exactly when present; an unqualified binding takes the first name
// match (the bus snapshot only carries the main database's tables today, per
// the resolver's own header note).
//
// sqlite's bus snapshot carries no routines at all (no stored
// procedures/functions — see `SnapshotTable`/`BuildArgs` in
// `sqliteResolver.ts`, which has no `snapshotRoutines` field). Per the T12
// task scope, `collectScalarRoutines` returns `[]` — routine discovery is
// NOT invented here. Built-in functions (`getSqliteBuiltinFunctions`) stay
// engine-emitted by the resolver branch itself, same as every sibling.
//
// `extraScalarSources` is OMITTED: the sqlite expression arm emits no
// scalar-valued non-column source today (no session/local variables, no
// bind-parameter discovery — grep of `sqliteResolver.ts` turns up nothing to
// reuse). Inventing one would violate the "reuse, never reimplement"
// constraint.

import type { AliasBinding, CteBinding, FkEdge } from '@/core/engines';
import type { ValueSlotInfo } from '@/features/sql-grammar/model';
import { inferSubqueryColumns } from '../resolver/inferSubqueryColumns';
import { resolveCteColumns } from '../resolver/resolveCteColumns';
import { resolveLhsInScope, type ScopedOwner } from '../valueSlot/scopedResolve';
import {
  coarseTypeOfDataType,
  isBooleanishDataType,
  type ValueSlotColumnDescriptor,
  type ValueSlotEngineAdapter,
  type ValueSlotRoutineDescriptor,
  type ValueSlotScope,
  type ValueSlotVirtualColumn,
} from '../valueSlot/types';
import { foldIdentifier, formatIdentifier } from './sqliteIdentifiers';

type AdapterTable = {
  readonly name: string;
  readonly schema?: string;
  readonly database?: string;
  readonly columns: ReadonlyArray<{ readonly name: string; readonly dataType: string }>;
};

/** Everything the adapter needs that is independent of the LHS operand. */
type SqliteAdapterSources = {
  readonly text: string;
  readonly aliasBindings: readonly AliasBinding[];
  readonly cteBindings: readonly CteBinding[];
  readonly snapshotTables: ReadonlyArray<AdapterTable>;
  readonly snapshotForeignKeys: readonly FkEdge[];
  /** C2 P1-1/P2-3 — resolver-supplied fallback for bindings whose relation
   *  is VIRTUAL: sqlite_master-family system tables and the json_each /
   *  json_tree TVFs' fixed column sets. The resolver passes a closure over
   *  its own static maps. Consulted only when the snapshot lookup yields
   *  nothing. */
  readonly virtualColumnsFor?: (binding: AliasBinding) => readonly ValueSlotVirtualColumn[] | undefined;
};

/** Scalar-value mode: a slot is REQUIRED. */
export type SqliteValueSlotContext = SqliteAdapterSources & {
  readonly slot: ValueSlotInfo;
};

/** Predicate-head mode (PH-D3, P1-sol F7): NO slot — a bare boolean-expression
 *  head has no LHS operand, so the scope resolves to
 *  `{ lhsType: 'unknown', lhsBooleanish: false }` and no column is ever
 *  flagged `fkPartnerOfLhs`. Declared as `slot?: undefined` rather than by
 *  omission so the overloads stay mutually exclusive: a caller passing a slot
 *  cannot land on this signature, and an existing scalar-value caller that
 *  DROPPED its slot fails to compile instead of silently degrading to
 *  no-LHS discovery. */
export type SqlitePredicateHeadContext = SqliteAdapterSources & {
  readonly slot?: undefined;
};

/** Implementation-side union — never the public parameter type. */
type SqliteAdapterContext = SqliteValueSlotContext | SqlitePredicateHeadContext;

/** Relation lookup — parity with the resolver's private `resolveRelation`
 *  (explicit database qualifier → exact match; else the first name match —
 *  sqlite has no search_path; the snapshot only carries the main
 *  database's tables). */
function lookupRelation(
  tables: ReadonlyArray<AdapterTable>,
  binding: AliasBinding,
): AdapterTable | undefined {
  const nameFolded = foldIdentifier(binding.relation);
  const dbFolded = binding.database
    ? foldIdentifier(binding.database)
    : binding.schema
      ? foldIdentifier(binding.schema)
      : null;
  if (dbFolded) {
    return tables.find(
      (t) => t.name.toLowerCase() === nameFolded && (t.database ?? '').toLowerCase() === dbFolded,
    );
  }
  return tables.find((t) => t.name.toLowerCase() === nameFolded);
}

function fkPartner(
  fks: readonly FkEdge[],
  lhs: { table: AdapterTable; column: string },
  cand: { table: AdapterTable; column: string },
): boolean {
  const match = (
    e: FkEdge,
    a: { table: AdapterTable; column: string },
    b: { table: AdapterTable; column: string },
  ) =>
    e.fromColumns.length === 1 &&
    e.fromTable.toLowerCase() === a.table.name.toLowerCase() &&
    e.fromColumns[0]!.toLowerCase() === a.column.toLowerCase() &&
    e.toTable.toLowerCase() === b.table.name.toLowerCase() &&
    e.toColumns[0]!.toLowerCase() === b.column.toLowerCase();
  return fks.some((e) => match(e, lhs, cand) || match(e, cand, lhs));
}

/** Resolve the slot's LHS to (owners, coarse type, boolean-ish) — the D5
 *  all-agree rule collapses disagreeing owners to 'unknown'. sqlite's
 *  snapshot carries its namespace on `database` (an ATTACHed-db alias), not
 *  `schema` — mapped into the shared resolver's `schema` slot so it lines up
 *  with the alias binding's own `schema`/`database` fallback chain (mirrors
 *  the mysql T10 adapter's identical `database ?? schema` mapping).
 *
 *  No slot (predicate-head mode, PH-D3) → no LHS → the neutral scope, via the
 *  same path a slot with an unresolvable LHS already took. */
function resolveLhs(args: SqliteAdapterContext): {
  owners: readonly ScopedOwner[];
  scope: ValueSlotScope;
} {
  const lhs = args.slot?.lhs;
  if (!lhs) return { owners: [], scope: { lhsType: 'unknown', lhsBooleanish: false } };
  const owners = resolveLhsInScope({
    lhs,
    aliasBindings: args.aliasBindings,
    cteBindings: args.cteBindings,
    snapshotTables: args.snapshotTables.map((t) => ({
      name: t.name,
      schema: t.database ?? t.schema,
      kind: 'table' as const,
      columns: t.columns.map((c) => ({
        name: c.name, dataType: c.dataType, nullable: true, isPrimaryKey: false, ordinal: 0,
      })),
    })),
    source: args.text,
    engineId: 'sqlite',
    fold: foldIdentifier,
  });
  const types = owners.map((o) =>
    coarseTypeOfDataType('dataType' in o.column ? o.column.dataType : undefined),
  );
  const allAgree = types.length > 0 && types.every((t) => t === types[0]);
  const bools = owners.map((o) =>
    isBooleanishDataType('dataType' in o.column ? o.column.dataType : undefined, 'sqlite'),
  );
  return {
    owners,
    scope: {
      lhsType: allAgree ? types[0]! : 'unknown',
      lhsBooleanish: bools.length > 0 && bools.every(Boolean),
    },
  };
}

export function buildSqliteValueSlotAdapter(args: SqliteValueSlotContext): {
  adapter: ValueSlotEngineAdapter;
  scope: ValueSlotScope;
};
export function buildSqliteValueSlotAdapter(args: SqlitePredicateHeadContext): {
  adapter: ValueSlotEngineAdapter;
  scope: ValueSlotScope;
};
export function buildSqliteValueSlotAdapter(args: SqliteAdapterContext): {
  adapter: ValueSlotEngineAdapter;
  scope: ValueSlotScope;
} {
  const { owners, scope } = resolveLhs(args);
  const lhsOwner = owners.length === 1 ? owners[0] : undefined;
  const lhsTable =
    lhsOwner && (lhsOwner.binding.kind === 'table' || lhsOwner.binding.kind === 'view')
      ? lookupRelation(args.snapshotTables, lhsOwner.binding)
      : undefined;
  const lhsColumn = lhsOwner ? lhsOwner.column.name : undefined;

  const collectScopedColumns = (): readonly ValueSlotColumnDescriptor[] => {
    const out: ValueSlotColumnDescriptor[] = [];
    for (const b of args.aliasBindings) {
      const spelling = b.aliasSql ?? b.alias;
      const push = (name: string, dataType: string | undefined, table?: AdapterTable) => {
        out.push({
          label: name,
          insertBare: formatIdentifier(name),
          insertQualified: `${spelling}.${formatIdentifier(name)}`,
          owner: b,
          coarseType: coarseTypeOfDataType(dataType),
          booleanish: isBooleanishDataType(dataType, 'sqlite'),
          fkPartnerOfLhs:
            table !== undefined &&
            lhsTable !== undefined &&
            lhsColumn !== undefined &&
            fkPartner(
              args.snapshotForeignKeys,
              { table: lhsTable, column: lhsColumn },
              { table, column: name },
            ),
        });
      };
      if (b.kind === 'subquery' || b.kind === 'derived') {
        const names =
          b.syntheticColumns && b.syntheticColumns.length > 0
            ? b.syntheticColumns
            : b.bodyRange
              ? inferSubqueryColumns({
                  source: args.text,
                  bodyRange: b.bodyRange,
                  engineId: 'sqlite',
                  snapshotTables: args.snapshotTables,
                  cteBindings: args.cteBindings,
                })
              : [];
        for (const n of names) push(n, undefined);
        if (names.length === 0) {
          for (const v of args.virtualColumnsFor?.(b) ?? []) push(v.name, v.dataType);
        }
        continue;
      }
      if (b.kind === 'cte') {
        const cte = args.cteBindings.find(
          (c) => foldIdentifier(c.name) === foldIdentifier(b.relation),
        );
        if (!cte) continue;
        const names = resolveCteColumns({
          cte,
          source: args.text,
          engineId: 'sqlite',
          snapshotTables: args.snapshotTables,
          cteBindings: args.cteBindings,
        });
        for (const n of names) push(n, undefined);
        continue;
      }
      const tbl = lookupRelation(args.snapshotTables, b);
      if (!tbl) {
        for (const v of args.virtualColumnsFor?.(b) ?? []) push(v.name, v.dataType);
        continue;
      }
      for (const c of tbl.columns) push(c.name, c.dataType, tbl);
    }
    return out;
  };

  // sqlite's bus snapshot carries no routines — see file header. Not
  // invented; the resolver branch's own `emitBuiltinFunctions(out)` call
  // stays the sole source of function candidates. PH-D4/F6's
  // `returnsBooleanish` promotion is consequently moot here: with zero
  // descriptors ever produced there is nothing to tag, on either policy.
  const collectScalarRoutines = (): readonly ValueSlotRoutineDescriptor[] =>
    collectSqliteScalarRoutineDescriptors();

  return {
    adapter: { engine: 'sqlite', collectScopedColumns, collectScalarRoutines },
    scope,
  };
}

/** Scalar-routine descriptor mapping shared by this adapter's
 *  `collectScalarRoutines` closure and the resolver's T18b typed-VALUES-
 *  element call site (`emitTypedValuesElement` in `sqliteResolver.ts`) —
 *  kept in one place so the two can't drift. Trivially `[]` — see file
 *  header (no routine discovery invented for sqlite). */
export function collectSqliteScalarRoutineDescriptors(): readonly ValueSlotRoutineDescriptor[] {
  return [];
}
